import { LinearProgress, Typography } from "@mui/material";
import type { ReactNode } from "react";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useProducts } from "../admin/products/useProducts";
import { useAuthContext } from "../auth/AuthContext";
import type { CalculationVersion, SavedCalculationVersion } from "../calculations/calculationTypes";
import { useCalculation } from "../calculations/useCalculation";
import ConfirmationDialog from "../components/confirmationDialog/ConfirmationDialog";
import ParticipantCountDiscrepancyDialog from "../components/participantCountDiscrepancyDialog/ParticipantCountDiscrepancyDialog";
import type { SaveDiscrepancies } from "../sharedComponents/participantCountValidation";
import type { CustomerListItem } from "../customers/customersApi";
import { useCustomerWithId } from "../customers/useCustomerWithId";
import dayjs, { ISO_DATE_FORMAT } from "../dayjs";
import { useCreateQuote } from "../quotes/useCreateQuote";
import type { CalculationDraft } from "./CalculationDraft";
import { CalculationEdit } from "./CalculationEdit";
import { useHandleSaveVersion } from "./utils/useHandleSaveVersion";

function draftFromSaved(
  calculation: NonNullable<ReturnType<typeof useCalculation>["calculation"]>,
  customer: CustomerListItem | null | undefined,
  versionIdx: number,
): CalculationDraft {
  const v = calculation.versions[versionIdx];
  return {
    calculationId: calculation.id,
    versionName: v.versionName,
    basicData: {
      name: v.name,
      startDate: dayjs(v.startDate),
      margin: v.margin ?? 0,
      peopleCount: v.participantsNumber.numberOfPaid,
      freePeopleCount: v.participantsNumber.numberOfStaff,
      additionalInfo: v.note ?? "",
      isPackageVersion: calculation.type === "PACKAGE",
      customer: customer ?? undefined,
    },
    packageId: v.packageId,
    currency: v.currency,
    products: v.products.map((p) => ({
      id: p.id,
      daysShift: p.daysShift ?? dayjs(p.date).diff(dayjs(v.startDate), "days"),
      options: p.options.map((o) => ({ id: o.id, quantity: o.quantity, price: o.price })),
    })),
  };
}

function draftToVersion(draft: CalculationDraft): CalculationVersion {
  const startDate = draft.basicData.startDate!;
  return {
    name: draft.basicData.name,
    versionName: draft.versionName,
    startDate: startDate.format(ISO_DATE_FORMAT),
    participantsNumber: {
      numberOfPaid: draft.basicData.peopleCount,
      numberOfStaff: draft.basicData.freePeopleCount,
    },
    packageId: draft.packageId,
    currency: draft.packageId ? undefined : draft.currency,
    margin: draft.packageId ? undefined : draft.basicData.margin,
    note: draft.basicData.additionalInfo || undefined,
    products: draft.products.map((p) => ({
      id: p.id,
      date: startDate.add(p.daysShift, "days").format(ISO_DATE_FORMAT),
      daysShift: p.daysShift,
      options: p.options.map((o) => ({ id: o.id, quantity: o.quantity, price: o.price })),
    })),
  };
}

export function EditCalculationPage() {
  const navigate = useNavigate();
  const { calculation, isLoading, onUpdate, onOrder, onDelete, onUpdatePrices } = useCalculation();
  const { context } = useAuthContext();
  const { customer, isLoading: isCustomerLoading } = useCustomerWithId(calculation?.customerId);
  const { isLoading: areProductsLoading } = useProducts();
  const [selectedVersionIdx, setSelectedVersionIdx] = useState<number | undefined>(undefined);
  const [isEdit, setIsEdit] = useState(false);
  const [draft, setDraft] = useState<CalculationDraft | undefined>(undefined);
  const { createQuote } = useCreateQuote();

  useEffect(() => {
    setSelectedVersionIdx(undefined);
  }, [calculation]);
  const [discrepancyModal, setDiscrepancyModal] = useState<SaveDiscrepancies | null>(null);
  const [pendingDraft, setPendingDraft] = useState<CalculationDraft | null>(null);
  useEffect(() => {
    if (isEdit) {
      return;
    }
    setDraft(
      calculation
        ? draftFromSaved(calculation, customer, selectedVersionIdx ?? calculation.versions.length - 1)
        : undefined,
    );
  }, [calculation, customer]);

  const [confirmationDialog, setConfirmationDialog] = useState<{
    title: string;
    content: ReactNode;
    onConfirm: () => Promise<void>;
  }>();

  const doCreateNewVersion = async (draft: CalculationDraft) => {
    await onUpdate(draftToVersion(draft), context!.role);
    setIsEdit(false);
  };

  const { handleSaveVersion } = useHandleSaveVersion(doCreateNewVersion, setDiscrepancyModal, setPendingDraft);

  if (isLoading || isCustomerLoading || areProductsLoading || !context) {
    return <LinearProgress />;
  }
  if (!calculation) {
    return <Typography variant="h5">Taka kalkulacja nie istnieje</Typography>;
  }

  const versionIdx = selectedVersionIdx ?? calculation.versions.length - 1;

  const onVersionChange = (id: number) => {
    setSelectedVersionIdx(id);
    setDraft(calculation ? draftFromSaved(calculation, customer, id) : undefined);
  };

  const handleEdit = () => {
    const newIdx = calculation.versions.length;
    setDraft((prev) => {
      if (!prev) {
        return prev;
      }
      return {
        ...prev,
        versionName: undefined,
      };
    });
    setSelectedVersionIdx(newIdx);
    setIsEdit(true);
  };

  const handleOrder = async (orderFromProviders: boolean, hasPlaneTickets: boolean) => {
    setConfirmationDialog({
      title: "Złóż zamówienie",
      content: (
        <>
          Czy chcesz złożyć zamówienie na podstawie kalkulacji? Kalkulacji nie będzie już można modyfikować.
          {hasPlaneTickets && (
            <>
              <br />
              <br />
              Po potwierdzeniu rezerwacji zawierającej bilety lotnicze anulacja jest możliwa wyłącznie odpłatnie - 350
              zł do 65 dni przed wylotem.
            </>
          )}
        </>
      ),
      onConfirm: async () => {
        const result = await onOrder(versionIdx + 1, orderFromProviders);
        setConfirmationDialog(undefined);
        if (result.orderId) {
          navigate(`/orders/${result.orderId}`);
        }
        navigate("/calculations");
      },
    });
  };

  const handleDelete = async () => {
    setConfirmationDialog({
      title: "Usuń ofertę",
      content: "Czy chcesz usunąć ofertę? Tej operacji nie będzie można już cofnąć.",
      onConfirm: async () => {
        await onDelete();
        setConfirmationDialog(undefined);
        navigate("/calculations");
      },
    });
  };

  const handleRequestQuote = async () => {
    setConfirmationDialog({
      title: "Zamów wycenę",
      content: "Czy chcesz wysłać prośbę do dostawcy o przygotowanie wyceny dla opcji 'na zapytanie'?",
      onConfirm: async () => {
        await createQuote({
          prepareFor: {
            type: "CALCULATION",
            id: calculation.id,
            calculationVersion: versionIdx + 1,
          },
        });
        setConfirmationDialog(undefined);
      },
    });
  };

  if (!draft) {
    return <LinearProgress />;
  }

  return (
    <>
      <CalculationEdit
        key={versionIdx}
        onEdit={handleEdit}
        handleRequestQuote={handleRequestQuote}
        onSubmitOrder={handleOrder}
        onDelete={handleDelete}
        onUpdatePrices={onUpdatePrices}
        value={draft}
        onSubmitVersion={handleSaveVersion}
        versions={calculation.versions as SavedCalculationVersion[]}
        selectedVersionIdx={versionIdx}
        onVersionChange={onVersionChange}
        context={context}
        isEditMode={isEdit}
        orderId={calculation?.orderId}
      />
      <ConfirmationDialog
        open={!!confirmationDialog}
        title={confirmationDialog?.title ?? ""}
        content={confirmationDialog?.content}
        onCancel={() => setConfirmationDialog(undefined)}
        onConfirm={confirmationDialog?.onConfirm}
      />
      <ParticipantCountDiscrepancyDialog
        discrepancies={discrepancyModal}
        onConfirm={async () => {
          if (pendingDraft) {
            await doCreateNewVersion(pendingDraft);
          }
          setDiscrepancyModal(null);
          setPendingDraft(null);
        }}
        onCancel={() => {
          setDiscrepancyModal(null);
          setPendingDraft(null);
        }}
      />
    </>
  );
}
